interface CachedResponse<T> {
  value: T;
  expiresAt: number;
}

interface Subscriber<T> {
  resolve: (value: T) => void;
  reject: (reason?: unknown) => void;
}

interface InflightRequest<T> {
  controller: AbortController;
  subscribers: Map<symbol, Subscriber<T>>;
  orphanAbortTimer: ReturnType<typeof setTimeout> | null;
}

const createAbortError = (): Error => {
  if (typeof DOMException !== "undefined") {
    return new DOMException("Aborted", "AbortError");
  }
  const err = new Error("Aborted");
  err.name = "AbortError";
  return err;
};

const removeOldestEntry = (map: Map<string, CachedResponse<unknown>>) => {
  const oldestKey = map.keys().next().value as string | undefined;
  if (oldestKey !== undefined) {
    map.delete(oldestKey);
  }
};

export interface RequestDedupCache {
  run<T>(args: {
    key: string;
    signal?: AbortSignal;
    load: (signal: AbortSignal) => Promise<T>;
  }): Promise<T>;
  clear(): void;
}

export function createRequestDedupCache(options?: {
  ttlMs?: number;
  maxEntries?: number;
  orphanAbortDelayMs?: number;
}): RequestDedupCache {
  const ttlMs = Math.max(0, options?.ttlMs ?? 20_000);
  const maxEntries = Math.max(1, options?.maxEntries ?? 100);
  const orphanAbortDelayMs = Math.max(0, options?.orphanAbortDelayMs ?? 150);

  const responseCache = new Map<string, CachedResponse<unknown>>();
  const inflight = new Map<string, InflightRequest<unknown>>();

  const readCached = <T,>(key: string): T | null => {
    const now = Date.now();
    const entry = responseCache.get(key);
    if (!entry) return null;
    if (entry.expiresAt <= now) {
      responseCache.delete(key);
      return null;
    }
    return entry.value as T;
  };

  const writeCached = <T,>(key: string, value: T) => {
    if (ttlMs <= 0) return;

    responseCache.delete(key);
    responseCache.set(key, {
      value,
      expiresAt: Date.now() + ttlMs,
    });

    while (responseCache.size > maxEntries) {
      removeOldestEntry(responseCache);
    }
  };

  const clear = () => {
    responseCache.clear();
    for (const entry of inflight.values()) {
      if (!entry.controller.signal.aborted) {
        entry.controller.abort();
      }
    }
    inflight.clear();
  };

  const run = <T,>(args: {
    key: string;
    signal?: AbortSignal;
    load: (signal: AbortSignal) => Promise<T>;
  }): Promise<T> => {
    if (args.signal?.aborted) {
      return Promise.reject(createAbortError());
    }

    const cached = readCached<T>(args.key);
    if (cached !== null) {
      return Promise.resolve(cached);
    }

    let entry = inflight.get(args.key) as InflightRequest<T> | undefined;
    if (entry?.controller.signal.aborted) {
      if (entry.orphanAbortTimer !== null) {
        clearTimeout(entry.orphanAbortTimer);
        entry.orphanAbortTimer = null;
      }
      inflight.delete(args.key);
      entry = undefined;
    }

    if (!entry) {
      const controller = new AbortController();
      entry = {
        controller,
        subscribers: new Map<symbol, Subscriber<T>>(),
        orphanAbortTimer: null,
      };
      inflight.set(args.key, entry as InflightRequest<unknown>);

      Promise.resolve()
        .then(() => args.load(controller.signal))
        .then((value) => {
          writeCached(args.key, value);
          const subscribers = Array.from(entry!.subscribers.values());
          entry!.subscribers.clear();
          subscribers.forEach((subscriber) => subscriber.resolve(value));
        })
        .catch((error) => {
          const subscribers = Array.from(entry!.subscribers.values());
          entry!.subscribers.clear();
          subscribers.forEach((subscriber) => subscriber.reject(error));
        })
        .finally(() => {
          if (entry!.orphanAbortTimer !== null) {
            clearTimeout(entry!.orphanAbortTimer);
            entry!.orphanAbortTimer = null;
          }
          inflight.delete(args.key);
        });
    }

    return new Promise<T>((resolve, reject) => {
      const subscriberId = Symbol(args.key);
      const activeEntry = entry as InflightRequest<T>;
      if (activeEntry.orphanAbortTimer !== null) {
        clearTimeout(activeEntry.orphanAbortTimer);
        activeEntry.orphanAbortTimer = null;
      }

      const cleanupAbortListener = () => {
        if (args.signal) {
          args.signal.removeEventListener("abort", onAbort);
        }
      };

      const resolveOnce = (value: T) => {
        cleanupAbortListener();
        resolve(value);
      };

      const rejectOnce = (error: unknown) => {
        cleanupAbortListener();
        reject(error);
      };

      const onAbort = () => {
        activeEntry.subscribers.delete(subscriberId);
        rejectOnce(createAbortError());
        if (
          activeEntry.subscribers.size === 0 &&
          !activeEntry.controller.signal.aborted
        ) {
          if (orphanAbortDelayMs <= 0) {
            activeEntry.controller.abort();
          } else {
            activeEntry.orphanAbortTimer = setTimeout(() => {
              if (
                activeEntry.subscribers.size === 0 &&
                !activeEntry.controller.signal.aborted
              ) {
                activeEntry.controller.abort();
              }
              activeEntry.orphanAbortTimer = null;
            }, orphanAbortDelayMs);
          }
        }
      };

      activeEntry.subscribers.set(subscriberId, {
        resolve: resolveOnce,
        reject: rejectOnce,
      });

      if (args.signal) {
        args.signal.addEventListener("abort", onAbort, { once: true });
        if (args.signal.aborted) {
          onAbort();
        }
      }
    });
  };

  return { run, clear };
}
